/**
 * Ticket-holder event chat — backend: event-chat.routes.ts (EventChatMessage/EventChatTopic).
 * Auth bridge: getCustomerToken() exchanges this Mango Personal session for a Customer session
 * scoped to the event's business (see utils/customerSession.ts), since event-chat access is
 * gated on holding an EventTicket, a Customer-side concept.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { eventChatApi, API_BASE_URL } from '../services/api';
import { uploadBlobThroughStorage, showToast } from 'shared-ui';
import { getCustomerToken } from '../utils/customerSession';

interface ChatMessage {
  id: string;
  content: string;
  photoUrl?: string | null;
  postedByType: string;
  authorName?: string | null;
  customer?: { id: string; firstName: string; lastName: string } | null;
  isAdmin: boolean;
  isStaff: boolean;
  isSavedByMe?: boolean;
  savedCount?: number;
  createdAt: string;
}

const EventChat: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('businessId') || '';

  const [token, setToken] = useState<string | null>(null);
  const [access, setAccess] = useState<{ canPost?: boolean; hasAccess?: boolean; reason?: string; welcomeMessage?: string; pinnedMessage?: string } | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    if (!eventId || !businessId) {
      setError('Missing event or business reference');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const t = await getCustomerToken(businessId);
      setToken(t);

      const accessRes = await eventChatApi.checkAccess(t, eventId);
      if (!accessRes?.success) throw new Error(accessRes?.error || 'Failed to check chat access');
      setAccess(accessRes.data);
      if (!accessRes.data?.hasAccess) {
        setError(accessRes.data?.reason || 'You do not have access to this chat');
        return;
      }

      const messagesRes = await eventChatApi.getMessages(t, eventId);
      if (messagesRes?.success) setMessages(messagesRes.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chat');
    } finally {
      setLoading(false);
    }
  }, [eventId, businessId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSend = async () => {
    if (!token || !eventId || !newMessage.trim()) return;
    setSending(true);
    try {
      const res = await eventChatApi.sendMessage(token, eventId, { content: newMessage.trim() });
      if (res?.success) {
        setNewMessage('');
        await load();
      } else {
        showToast(res?.error || 'Failed to send message', 'error');
      }
    } finally {
      setSending(false);
    }
  };

  const handleAttachPhotos = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !token || !eventId) return;
    setUploadingPhotos(true);
    try {
      for (const file of Array.from(fileList)) {
        const uploaded = await uploadBlobThroughStorage({
          apiBaseUrl: API_BASE_URL,
          tokenKey: 'mangoPersonalToken',
          file,
          meta: { fileType: 'ATTACHMENT' },
        });
        if (!uploaded.url) continue;
        // One EventChatMessage per photo — the model carries a single photoUrl, not an array.
        await eventChatApi.sendMessage(token, eventId, { photoUrl: uploaded.url });
      }
      await load();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to upload photos', 'error');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const toggleSave = async (message: ChatMessage) => {
    if (!token || !eventId) return;
    const optimistic = !message.isSavedByMe;
    setMessages((prev) =>
      prev.map((m) => (m.id === message.id ? { ...m, isSavedByMe: optimistic, savedCount: (m.savedCount || 0) + (optimistic ? 1 : -1) } : m))
    );
    const res = optimistic
      ? await eventChatApi.saveMessage(token, eventId, message.id)
      : await eventChatApi.unsaveMessage(token, eventId, message.id);
    if (!res?.success) await load(); // revert via refetch on failure
  };

  if (loading) {
    return <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px', color: '#666' }}>Loading chat…</div>;
  }

  if (error) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px' }}>
        <Link to="/tickets" style={{ fontSize: '13px', color: '#667eea' }}>&larr; Back to tickets</Link>
        <div style={{ marginTop: '16px', padding: '20px', background: '#fef2f2', color: '#b91c1c', borderRadius: '12px' }}>{error}</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '24px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)' }}>
      <Link to="/tickets" style={{ fontSize: '13px', color: '#667eea', marginBottom: '8px' }}>&larr; Back to tickets</Link>
      <h1 style={{ margin: '0 0 4px 0', fontSize: '22px' }}>💬 Event Chat</h1>
      {access?.welcomeMessage && <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>{access.welcomeMessage}</p>}

      <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
        Photos you share here may be used by the event organizer — see their event terms. Unsaved
        photos are removed a few days after the event; tap 📌 Save on anything you want to keep.
      </p>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', paddingBottom: '16px' }}>
        {messages.length === 0 && <p style={{ color: '#999', fontSize: '14px' }}>No messages yet — be the first to share something.</p>}
        {messages.map((msg) => (
          <div key={msg.id} style={{ background: '#f8fafc', borderRadius: '10px', padding: '10px 14px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
              <strong>
                {msg.isAdmin || msg.isStaff ? (msg.authorName || 'Organizer') : msg.customer ? `${msg.customer.firstName} ${msg.customer.lastName}` : 'Attendee'}
              </strong>
              <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            {msg.content && <p style={{ margin: '0 0 6px 0', fontSize: '14px' }}>{msg.content}</p>}
            {msg.photoUrl && (
              <div>
                <img src={msg.photoUrl} alt="" style={{ maxWidth: '100%', maxHeight: '320px', borderRadius: '8px', display: 'block', marginBottom: '6px' }} />
                <button
                  type="button"
                  onClick={() => toggleSave(msg)}
                  style={{ padding: '4px 10px', background: msg.isSavedByMe ? '#16a34a' : '#e2e8f0', color: msg.isSavedByMe ? 'white' : '#333', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                >
                  📌 {msg.isSavedByMe ? 'Saved' : 'Save'} {msg.savedCount ? `(${msg.savedCount})` : ''}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {access?.canPost && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={uploadingPhotos}
            title="Attach photos"
            style={{ width: '40px', height: '40px', background: '#f1f5f9', border: 'none', borderRadius: '50%', cursor: uploadingPhotos ? 'not-allowed' : 'pointer', fontSize: '18px' }}
          >
            {uploadingPhotos ? '…' : '📎'}
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => { handleAttachPhotos(e.target.files); e.target.value = ''; }}
          />
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Message the event chat"
            style={{ flex: 1, padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '20px', fontSize: '14px' }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !newMessage.trim()}
            style={{ padding: '10px 18px', background: newMessage.trim() ? '#667eea' : '#e2e8f0', color: 'white', border: 'none', borderRadius: '20px', cursor: newMessage.trim() ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: '14px' }}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
};

export default EventChat;
